/** @import {NewAccountExplorerProps, SummaryBoxProductData, SummaryCopy} from './NewAccountExplorerTypes' */
/** @import {WithFeatureFlagsProps} from '../FeatureFlags/FeatureFlagsTypes' */
/** @import {Product} from '../../contexts/CustomerDataProviderTypes' */

import React, { useEffect, useContext, useState } from 'react';
import './newAccounts.scss';

import { formatDate } from '../../utils/helpers';

import Button from '../Form/Button/Button';
import SummaryBox from './SummaryBox/SummaryBox';
import Loading from '../Loading/Loading';
import envConfig from '../../config/env-config';

import { customerDataContext } from '../../contexts/CustomerDataProvider';
import { calculations } from "./SavingsCalculator/calculations";
import { mapProductTypeFromUrl, productKeys, productTypes } from '../../utils/productHelpers';
import infoIcon from "../../assets/images/info-icon.svg";
import {
    convertProductTermToMonths,
    formatProductTerm,
    formatCurrency,
    existingApplicationAcceptedInSession
} from "../../utils/helpers";
import { AccordionLink } from "../Accordion/Accordion";

import withFeatureFlags from '../../components/FeatureFlags/withFeatureFlags';
import {useUnifiedAnalytics} from "../../contexts/AnalyticsWrapper";
import IsaTransferSection from './IsaTransferSection/IsaTransferSection';

const ApplicationsDisabledNotice = ({ className = '' }) => (
    <div className={`funding-source-selection__notice ${className}`.trim()}>
        <div className="funding-source-selection__info-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.8104 1.66837C12.2931 1.43834 11.7025 1.43834 11.1852 1.66837C10.7856 1.84602 10.5261 2.15894 10.3455 2.41391C10.1679 2.66461 9.97784 2.99303 9.76877 3.35422L1.50161 17.6339C1.2917 17.9964 1.10095 18.3258 0.971609 18.6054C0.840159 18.8895 0.697554 19.2714 0.743003 19.7074C0.801814 20.2715 1.09736 20.7841 1.55607 21.1176C1.91057 21.3753 2.31256 21.4433 2.62432 21.4719C2.93107 21.5 3.31173 21.5 3.73061 21.5H20.2649C20.6838 21.5 21.0645 21.5 21.3712 21.4719C21.683 21.4433 22.085 21.3753 22.4395 21.1176C22.8982 20.7841 23.1937 20.2715 23.2525 19.7074C23.298 19.2714 23.1554 18.8895 23.0239 18.6054C22.8946 18.3258 22.7039 17.9964 22.494 17.6339L14.2268 3.35419C14.0177 2.99301 13.8276 2.66459 13.65 2.41391C13.4694 2.15894 13.2099 1.84602 12.8104 1.66837ZM12.9978 9V13C12.9978 13.5523 12.5501 14 11.9978 14C11.4455 14 10.9978 13.5523 10.9978 13V9C10.9978 8.44772 11.4455 8 11.9978 8C12.5501 8 12.9978 8.44772 12.9978 9ZM11.9978 16H12.0078C12.5601 16 13.0078 16.4477 13.0078 17C13.0078 17.5523 12.5601 18 12.0078 18H11.9978C11.4455 18 10.9978 17.5523 10.9978 17C10.9978 16.4477 11.4455 16 11.9978 16Z" fill="#BE732F"/>
            </svg>
        </div>
        <div className="funding-source-selection__info-content">
            <div className="funding-source-selection__notice-body">We're temporarily pausing new applications for this account. We'll be opening them again soon.</div>
        </div>
    </div>
);

function NewAccountExplorer(/** @type {NewAccountExplorerProps & WithFeatureFlagsProps} */ { data, featureEnabled }) {
    const customerData = useContext(customerDataContext);
    const [countryListTooltip, setCountryListTooltip] = useState(false);
    const [infoTop, setInfoTop] = useState(0);
    const [infoLeft, setInfoLeft] = useState(0);
    const popupRef = React.createRef();

    const analytics = useUnifiedAnalytics();

    useEffect(() => {
        /**
         * Function to handle clicks outside the tooltip
         * @param {MouseEvent} event - The mouse event
         */
        const handleClickOutside = (event) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(/** @type {Node} */(event.target)) &&
                event.target instanceof Element &&
                !event.target.closest('.newaccounts__img')
            ) {
                setCountryListTooltip(false);
            }
        };

        // Attach the event listener to the document
        document.addEventListener('click', handleClickOutside);

        // Clean up the event listener on component unmount
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [popupRef]);


    const isSecondaryAccountEnabled = (/** @type {string} */ productType) => {
        if (featureEnabled('SecondaryAccountAppsOffline'))
            return false;

        return (productType === productTypes.EasyAccess && !featureEnabled('SecondaryAccountOFSDisabled'))
            || (productType === productTypes.Notice && !featureEnabled('SecondaryAccountNSDisabled'))
            || (productType === productTypes.FixedTerm && !featureEnabled('SecondaryAccountFRSDisabled'))
            || (productType === productTypes.FixedRateCashISA && !featureEnabled('SecondaryAccountFISADisabled'));
    }

    const togglePopupFrsSummary = (/** @type {React.MouseEvent<HTMLImageElement, MouseEvent>} */ event) => {
        event.preventDefault();
        event.stopPropagation();

        let button = event.currentTarget;
        const tooltipImage = document.getElementById('imgCheckListSummary');
        const rect = tooltipImage?.getBoundingClientRect();
        let prect = button.offsetParent?.getBoundingClientRect();

        if (prect) {
            setCountryListTooltip(!countryListTooltip);
            setInfoTop((rect?.top || 0) - prect.top + 31);
            setInfoLeft((rect?.left || 0) - prect.left - 0);
        }
    };

    const togglePopupOfsSummary = (/** @type {React.MouseEvent<HTMLImageElement, MouseEvent>} */ event) => {
        event.preventDefault();
        event.stopPropagation();

        let button = event.currentTarget;
        const tooltipImage = document.getElementById('imgCheckListSummary');
        const rect = tooltipImage?.getBoundingClientRect();
        let prect = button.offsetParent?.getBoundingClientRect();

        if (prect) {
            setCountryListTooltip(!countryListTooltip);
            setInfoTop((rect?.top || 0) - prect.top + 31);
            setInfoLeft((rect?.left || 0) - prect.left - 0);
        }
    };

    const togglePopupNsSummary = (/** @type {React.MouseEvent<HTMLImageElement, MouseEvent>} */ event) => {
        event.preventDefault();
        event.stopPropagation();

        let button = event.currentTarget;
        const tooltipImage = document.getElementById('imgCheckListSummary');
        const rect = tooltipImage?.getBoundingClientRect();
        let prect = button.offsetParent?.getBoundingClientRect();

        if (prect) {
            setCountryListTooltip(!countryListTooltip);
            setInfoTop((rect?.top || 0) - prect.top + 31);
            setInfoLeft((rect?.left || 0) - prect.left - 0);
        }
    };

    const getrateChangeNotification = (/** @type {SummaryBoxProductData} */ product) => {
        var notifiction = ((product.rateChanges != null && product.rateChanges.length === 2) ?
            "Please note, the interest rate for this account will decrease from " + products[0].aerRate + "% AER* / " + products[0].rate + "% gross p.a. to "
            // @ts-expect-error - cater for nulls
            + (Math.round(product.rateChanges[1].aerRate * 100) / 100).toFixed(2) + "% AER* / " + (Math.round(product.rateChanges[1].rate * 100) / 100).toFixed(2) + "% gross p.a. effective " + formatDate(new Date(product.rateChanges[1].scheduledFor)) + ".\n The rate will further decrease to "
            // @ts-expect-error - cater for nulls
            + (Math.round(product.rateChanges[0].aerRate * 100) / 100).toFixed(2) + "% AER* / " + (Math.round(product.rateChanges[0].rate * 100) / 100).toFixed(2) + "% gross p.a. effective " + formatDate(new Date(product.rateChanges[0].scheduledFor)) + ".\n"
            : ((product.rateChanges != null && product.rateChanges.length === 1) ?
                "Please note, the interest rate for this account will decrease from " + products[0].aerRate + "% AER* / " + products[0].rate + "% gross p.a. to "
                // @ts-expect-error - cater for nulls
                + (Math.round(product.rateChanges[0].aerRate * 100) / 100).toFixed(2) + "% AER* / " + (Math.round(product.rateChanges[0].rate * 100) / 100).toFixed(2) + "% gross p.a. effective " + formatDate(new Date(product.rateChanges[0].scheduledFor)) + ".\n"
                : ""))
        return notifiction;
    };

    /** @type {SummaryCopy[]} */
    const summaryCopy = [
        {
            productType: productTypes.FixedTerm,
            summaries: [
                {
                    title: 'Account name',
                    body: function (products) {
                        return (<p>Fixed Rate Saver</p>)
                    }
                },
                {
                    title: 'What is the interest rate?',
                    body: function (products) {
                        if (!products || products.length === 0) return null;

                        return <div>
                            <div className={'summary-box-table interest table-border'}>
                                <div className={'heading'}>Term</div>
                                <div className={'heading'}>Annual interest rate</div>
                                <div className={'heading'}>Interest payments</div>
                                {products.map(p => <>
                                    <div key={p.name} className={'content'}>{formatProductTerm(p.productTerm)}</div>
                                    <div key={p.rate} className={'content'}>{p.rate}% AER* / gross p.a.</div>
                                    <div key={p.code} className={'content'}>{p.code === productKeys.FixedRateSaver ? "On maturity" : "Annually"}</div>
                                </>)}
                            </div>
                            <p className="margin-sml-top">The example above is illustrative only and is not based on individual circumstances. It also assumes the deposit is made on the day the account is opened.</p>

                        </div>
                    }
                },
                {
                    title: 'Can Investec change the interest rate?',
                    body: function (products, featureEnabled) {
                        return (<p>No, the interest rate is fixed for the term. The interest rate you receive will be the rate at the time
                            you applied for your account.</p>)
                    }
                },
                {
                    title: 'What would the estimated balance be at the end of the term based on a ' + formatCurrency(5000, false) + ' deposit?',
                    body: function (products, featureEnabled) {
                        if (!products || products.length === 0) return null;

                        return <div>
                            <p>At current interest rates:</p>
                            <div className={'summary-box-table table-border example-calculation'}>
                                <div className={'heading narrow'}><p>Term</p></div>
                                <div className={'heading'}><p>Deposit</p></div>
                                <div className={'heading'}><p>Balance at the end of the term</p></div>

                                {products.map(p => <>
                                    <div className={'content narrow'}>{formatProductTerm(p.productTerm)}</div>
                                    <div className={'content'}>{formatCurrency(5000)}</div>
                                    <div className={'content'}>{formatCurrency(p.calculated.saved)}</div>
                                </>)}
                            </div>
                            <p className="margin-sml-top">The example above is illustrative only and isn't based on individual
                                circumstances. It also assumes the deposit is made on the day the account is opened. The example
                                shows total interest earned, please note that interest will be paid annually.</p>
                        </div>
                    }
                },
                {
                    title: 'How do I open and manage my account?',
                    body: function (products, featureEnabled) {
                        return (
                            <div>
                                <p className="margin-sml-top">
                                    This fixed term savings account is opened and managed online. How you apply depends on whether or not you already have an Investec 90-Day Notice Saver, Fixed Rate Saver or Online Flexi Saver.
                                </p>
                                <p className="margin-sml-top">
                                    If you have or have had any of the above, you can apply for another account via your online dashboard.
                                </p>
                                <p className="margin-sml-top">If you <b>don't</b>, you'll need to have the following to hand to apply for one:
                                    <ul>
                                        <li>The sort code and account number of your UK current account (this will be your linked account)</li>
                                        <li>Your UK smartphone</li>
                                        <li>
                                            <div>
                                                A passport or photocard driving licence from any country we currently support
                                                <img
                                                    alt="info"
                                                    className="newaccounts__img"
                                                    src={infoIcon}
                                                    onClick={togglePopupFrsSummary}
                                                    id='imgCheckListSummary'
                                                />
                                            </div>
                                            <div>
                                                {countryListTooltip && <div ref={popupRef}
                                                    className={['summary-box__tooltip-content', countryListTooltip ? '' : ''].join(' ')}
                                                    style={{ top: infoTop, left: infoLeft }}>
                                                    For a full list of countries we can accept identification from, please see our <AccordionLink href={envConfig.links.productPageUrl + '/faqs#194d0f0f-b713-47d2-b5f2-f98bde466247'} target="_blank">FAQs</AccordionLink>
                                                </div>}
                                            </div>
                                        </li>
                                    </ul>
                                </p>
                                <p className="margin-sml-top">To be eligible for an account you will need to:
                                    <ul>
                                        <li>Be 18 or over</li>
                                        <li>Have a UK residential address</li>
                                        <li>Have between {formatCurrency(5000, false)}-{formatCurrency(250000, false)} to save</li>
                                    </ul>
                                </p>
                                <p><b>Fund your account in the first seven days</b></p>
                                <p className="margin-sml-top">
                                    {!featureEnabled("EnableFundsTransfer") &&
                                        <>Once your account is open, you'll have seven calendar days to fund it with at least {formatCurrency(5000, false)} from your linked account. </>}
                                    {featureEnabled("EnableFundsTransfer") &&
                                        <>Once your account is open, you'll have seven calendar days to fund it with at least {formatCurrency(5000, false)} from your linked account, or
                                            if you have one, you can transfer money directly from your Online Flexi Saver. </>}
                                    You can make unlimited deposits during this period up to a total balance of {formatCurrency(250000, false)}. If you
                                    don't meet the minimum balance by the end of the seven days, we'll return any money and interest earnings and close your
                                    account automatically.
                                </p>
                                <p className="margin-sml-top">
                                    <b>Please note:</b>
                                </p>
                                <p className="margin-sml-top">
                                    We only accept applications for individual accounts.
                                </p>
                            </div>
                        )
                    }
                },
                {
                    title: 'Can I withdraw money?',
                    body: function (products, featureEnabled) {
                        return (
                            <div>
                                <p className="margin-sml-top">You won't be able to make any withdrawals from this account for the full
                                    duration of your fixed term, including the seven-day funding window. Your interest is paid annually
                                    into your linked account. At maturity, your final portion of interest and your balance are paid in
                                    one fixed payment to your chosen account.</p>
                                <p className="margin-sml-top">Please note there is no 14-day cooling off period for this account.</p>
                            </div>
                        )
                    }
                },
                {
                    title: <span>Additional information</span>,
                    body: function (products, featureEnabled) {
                        return (<p>Interest will be paid without tax being deducted. Your tax treatment will depend on your individual
                            circumstances. Further guidance can be found on the HMRC website.</p>)
                    }
                }
            ]
        },
        {
            productType: productTypes.EasyAccess,
            summaries: [
                {
                    title: 'Account name',
                    body: function (products) {
                        if (!products || products.length === 0) return null;

                        const product = products[0];
                        return (<p>{product.name}</p>)
                    }
                },
                {
                    title: 'What is the interest rate?',
                    body: function (products) {
                        if (!products || products.length === 0) return null;

                        const product = products[0];
                        return (
                            <div>
                                {featureEnabled('EnableInterimRateDecrease') && (getrateChangeNotification(product) &&
                                    <p className="rate-change-emphasis">{getrateChangeNotification(product)}</p>)}
                                {!featureEnabled('EnableInterimRateDecrease') && (product.rateChangeNotification &&
                                    <p className="rate-change-emphasis">{product.rateChangeNotification}</p>)}
                                <table cellSpacing="0">
                                    <tr>
                                        <th>Account balance</th>
                                        <th>AER* / gross p.a. (variable)</th>
                                    </tr>
                                    <tr>
                                        <td>{formatCurrency(0, false)} - {formatCurrency(4999, false)}</td>
                                        <td>0.00%</td>
                                    </tr>
                                    <tr>
                                        <td>{formatCurrency(5000, false)} - {formatCurrency(250000, false)}</td>
                                        <td>{product.rate}% {product.aerRate ? `/ ${product.aerRate}%` : ''}</td>
                                    </tr>
                                </table>
                                <p className="margin-sml-top">You'll earn {product.rate}% gross p.a.^ on your <b>entire</b> balance when it's between £5,000-£250,000.</p>
                                <p className="margin-sml-top">However, if it's equal to or under £4,999.99, you won't earn any interest.</p>
                                <p className="margin-sml-top">Interest is calculated daily and paid monthly on the 1st.</p>
                                <p className="margin-sml-top">Interest can be added to your Online Flexi Saver or paid to your linked
                                    account (which can be any UK current account held in your name).</p>
                            </div>)
                    }
                },
                {
                    title: 'Can Investec change the interest rate?',
                    body: function (products, featureEnabled) {
                        return (
                            <div>
                                <p>Yes, the interest rate is variable – we'll always give you advance notice if we decrease it. For more details on how the rate can change and how we'll let you know, visit our product terms.</p>
                            </div>)
                    }
                },
                {
                    title: 'What would the estimated balance be after 12 months based on a ' + formatCurrency(5000, false) + ' deposit?',
                    body: function (products, featureEnabled) {
                        if (!products || products.length === 0) return null;

                        const product = products[0];
                        return (
                            <div>
                                <p>At current interest rates, a deposit of {formatCurrency(5000, false)} would generate interest of {formatCurrency(product.calculated.interest, true)} after 12 months. This
                                    would provide a final balance of {formatCurrency(product.calculated.saved, true)}.</p>
                                <p className="margin-sml-top">
                                    <p>Please note that:</p>
                                    <ul>
                                        <li>If your account balance falls below {formatCurrency(5000, false)}, you'll earn 0.00% gross p.a. (variable)/AER*
                                        </li>
                                    </ul>
                                </p>
                                <p className="margin-sml-top">
                                    <p>This example above is illustrative only and is not based on individual
                                        circumstances. It also assumes:</p>
                                    <ul>
                                        <li>No withdrawals or further deposits are made</li>
                                        <li>Interest is paid into the Online Flexi Saver</li>
                                        <li>There's no change to the interest rate</li>
                                    </ul>
                                </p>
                            </div>)
                    }
                },
                {
                    title: 'How do I open and manage my account?',
                    body: function (products, featureEnabled) {
                        return (
                            <div>
                                <p className="margin-sml-top">
                                    This instant access savings account is opened and managed online. How you apply depends on whether or not you already have
                                    an Investec 90-Day Notice Saver, Fixed Rate Saver or Online Flexi Saver.
                                </p>
                                <p className="margin-sml-top">
                                    If you have or have had any of the above, you can apply for another account via your online dashboard.
                                </p>
                                <p className="margin-sml-top">If you <b>don't</b>, you'll need to have the following to hand to apply for one:
                                    <ul>
                                        <li>The sort code and account number of your UK current account (this will be your linked account)</li>
                                        <li>Your UK smartphone</li>
                                        <li>
                                            <div>
                                                A passport or photocard driving licence from any country we currently support
                                                <img
                                                    alt="info"
                                                    className="newaccounts__img"
                                                    src={infoIcon}
                                                    onClick={togglePopupOfsSummary}
                                                    id='imgCheckListSummary'
                                                />
                                            </div>
                                            <div>
                                                {countryListTooltip && <div ref={popupRef}
                                                    className={['summary-box__tooltip-content', countryListTooltip ? '' : ''].join(' ')}
                                                    style={{ top: infoTop, left: infoLeft }}>
                                                    For a full list of countries we can accept identification from, please see our <AccordionLink href={envConfig.links.productPageUrl + '/faqs#194d0f0f-b713-47d2-b5f2-f98bde466247'} target="_blank">FAQs</AccordionLink>
                                                </div>}
                                            </div>
                                        </li>
                                    </ul>
                                </p>
                                <p className="margin-sml-top">To be eligible for an account you will need to:
                                    <ul>
                                        <li>Be 18 or over</li>
                                        <li>Have a UK residential address</li>
                                        <li>Have between {formatCurrency(5000, false)}-{formatCurrency(250000, false)} to save</li>
                                    </ul>
                                </p>
                                <p className="margin-sml-top">
                                    <b>Please note:</b>
                                </p>
                                <p className="margin-sml-top">
                                    We only accept applications for individual accounts.
                                </p>
                            </div>
                        )
                    }
                },
                {
                    title: 'Can I withdraw money?',
                    body: function (products, featureEnabled) {
                        return (
                            <div>
                                <p>Yes, you can do this online 24/7/365. This is an instant access savings account, with no restrictions
                                    on the number of withdrawals (or deposits).</p>
                                <p className="margin-sml-top">To move money in and out of your account, you need to link it to a UK
                                    current account held in your name. You'll only be able to pay money into and out of your Online
                                    Flexi Saver from this linked account. </p>
                            </div>)
                    }
                },
                {
                    title: 'Additional Information',
                    body: function (products, featureEnabled) {
                        return (<p>Interest will be paid without tax being deducted. Your tax treatment will depend on your individual
                            circumstances. Further guidance can be found on the HMRC website.</p>)
                    }
                },
            ]
        },
        {
            productType: productTypes.Notice,
            summaries: [
                {
                    title: 'Account name',
                    body: function (products) {
                        if (!products || products.length === 0) return null;

                        const product = products[0];
                        return (<p>{`${product.name} Issue ${product.issueNumber}`}</p>)
                    }
                },
                {
                    title: 'What is the interest rate?',
                    body: function (products, featureEnabled) {
                        if (!products || products.length === 0) return null;

                        const product = products[0];
                        return (
                            <div>
                                {product.rateChangeNotification &&
                                    <p className="rate-change-emphasis">{product.rateChangeNotification}</p>}
                                <table cellSpacing="0">
                                    <tr>
                                        <th>If your balance is between:</th>
                                        <th>AER* / gross p.a. (variable)</th>
                                    </tr>
                                    <tr>
                                        <td>{formatCurrency(0, false)} - {formatCurrency(4999, false)}</td>
                                        <td>0.00%</td>
                                    </tr>
                                    <tr>
                                        <td>{formatCurrency(5000, false)} - {formatCurrency(250000, false)}</td>
                                        <td>{product.aerRate ? `${product.aerRate}% /` : ''} {product.rate}% </td>
                                    </tr>
                                </table>
                                <p className="margin-sml-top">You'll earn {product.rate}% gross p.a.^ on your <b>entire</b> balance when it's between £5,000-£250,000.</p>
                                <p className="margin-sml-top">However, if it's equal to or under £4,999.99, you won't earn any interest.</p>
                                <p className="margin-sml-top">Interest is calculated daily and paid monthly on the 15th.</p>
                                <p className="margin-sml-top">Interest can be added to your Notice Saver or paid to your linked account (which can be any UK current account held in your name).
                                </p>
                            </div>)
                    }
                },
                {
                    title: 'Can Investec change the interest rate?',
                    body: function (products, featureEnabled) {
                        return (
                            <p>Yes, the interest rate is variable - we'll always give you advance notice if we decrease it. For more details on how the rate can change and how we'll let you know, visit our product terms.</p>
                        )
                    }
                },
                {
                    title: 'What would the estimated balance be after 12 months based on a ' + formatCurrency(5000, false) + ' deposit?',
                    body: function (products, featureEnabled) {
                        if (!products || products.length === 0) return null;

                        return (
                            <div>
                                <p>At current interest rates, a deposit of {formatCurrency(5000, false)} would generate interest of {formatCurrency(products[0].calculated.interest, true)} after 12 months. This
                                    would provide a final balance of {formatCurrency(products[0].calculated.saved, true)}.</p>
                                <p className="margin-sml-top">
                                    <p>Please note that:</p>
                                    <ul>
                                        <li>If your account balance falls below {formatCurrency(5000, false)}, you'll earn 0.00% gross p.a. (variable)/AER*
                                        </li>
                                    </ul>
                                </p>
                                <p className="margin-sml-top">
                                    <p>This example is illustrative only and is not based on individual
                                        circumstances. It also assumes:</p>
                                    <ul>
                                        <li>No withdrawals or further deposits are made</li>
                                        <li>Interest is paid into the 90-Day Notice Saver</li>
                                        <li>There's no change to the interest rate</li>
                                    </ul>
                                </p>
                            </div>)
                    }
                },
                {
                    title: 'How do I open and manage my account?',
                    body: function (products, featureEnabled) {
                        return (
                            <div>
                                <p className="margin-sml-top">
                                    This 90-Day Notice Saver account is opened and managed online. How you apply depends on whether or not you already have an Investec 90-Day Notice Saver, Fixed Rate Saver or Online Flexi Saver.
                                </p>
                                <p className="margin-sml-top">
                                    If you have or have had any of the above, you can apply for another account via your online dashboard.
                                </p>
                                <p className="margin-sml-top">If you <b>don't</b>, you'll need to have the following to hand to apply for one:
                                    <ul>
                                        <li>The sort code and account number of your UK current account (this will be your linked account)</li>
                                        <li>Your UK smartphone</li>
                                        <li>
                                            <div>
                                                A passport or photocard driving licence from any country we currently support
                                                <img
                                                    alt="info"
                                                    className="newaccounts__img"
                                                    src={infoIcon}
                                                    onClick={togglePopupNsSummary}
                                                    id='imgCheckListSummary'
                                                />
                                            </div>
                                            <div>
                                                {countryListTooltip && <div ref={popupRef}
                                                    className={['summary-box__tooltip-content', countryListTooltip ? '' : ''].join(' ')}
                                                    style={{ top: infoTop, left: infoLeft }}>
                                                    For a full list of countries we can accept identification from, please see our <AccordionLink href={envConfig.links.productPageUrl + '/faqs#194d0f0f-b713-47d2-b5f2-f98bde466247'} target="_blank">FAQs</AccordionLink>
                                                </div>}
                                            </div>
                                        </li>
                                    </ul>
                                </p>
                                <p className="margin-sml-top">To be eligible for an account you will need to:
                                    <ul>
                                        <li>Be 18 or over</li>
                                        <li>Have a UK residential address</li>
                                        <li>Have between {formatCurrency(5000, false)}-{formatCurrency(250000, false)} to save</li>
                                    </ul>
                                </p>
                                <p className="margin-sml-top">
                                    <b>Please note:</b>
                                </p>
                                <p className="margin-sml-top">
                                    We only accept applications for individual accounts.
                                </p>
                            </div>
                        )
                    }
                },
                {
                    title: 'Can I withdraw money?',
                    body: function (products, featureEnabled) {
                        return (
                            <div>
                                <p>Yes, you can schedule partial or full withdrawals online 24/7/365, either to your linked account or your Online Flexi Saver if you have one.</p>
                                <p className="margin-sml-top">All withdrawals are subject to the 90-day notice period, which will start on the calendar day that you tell us you'd like to make a withdrawal. You'll still earn interest during this period.</p>
                                <p className="margin-sml-top">You can cancel a withdrawal at any time up until 20:00 the evening before your notice period ends.</p>
                            </div>)
                    }
                },
                {
                    title: 'Additional Information',
                    body: function (products, featureEnabled) {
                        return (<div>
                            <p>Interest will be paid without tax being deducted. Your tax treatment will depend on your individual circumstances. Further guidance can be found on the HMRC website.</p>
                            <p className="margin-sml-top">This account comes with a 14-day cooling off period. This means that if you change your mind at any time during the first 14 days after you open it, you can close it and withdraw your entire balance, including any accrued interest, without needing to wait for the 90-day notice period to end.</p>
                        </div>)
                    }
                },
            ]
        },
        {
            productType: productTypes.FixedRateCashISA,
            summaries: [
                {
                    title: 'What is the interest rate?',
                    body: function (products) {
                        if (!products || products.length === 0) return null;

                        const product = products[0];
                        return (
                            <div className={'summary-box-table__container'}>
                                <div className={'summary-box-table interest table-border'}>
                                    <div className={'heading'}><p>Term</p></div>
                                    <div className={'heading'}><p>Annual interest rate</p></div>
                                    <div className={'heading'}><p>Interest payments</p></div>
                                    <div className={'content'}>1 year</div>
                                    <div className={'content'}>
                                        {product && product.rate ? `${product.rate}% AER* / gross p.a.` : 'Competitive rates available'}
                                    </div>
                                    <div className={'content'}>
                                        {product.code === productKeys.FixedRateCashISA ? "On maturity" : "Annually"}
                                    </div>
                                </div>
                                <p className="margin-sml-top">Interest is calculated daily and paid annually.</p>
                            </div>
                        )
                    }
                },
                {
                    title: 'Can Investec change the interest rate?',
                    body: function (products, featureEnabled) {
                        return (<p>No, the interest rate is fixed for the term. The interest rate you receive will be the rate at the time
                            you applied for your account.</p>)
                    }
                },
                {
                    title: 'What would the estimated balance be after 12 months based on a £1,000 deposit?',
                    body: function (products, featureEnabled) {
                        if (!products || products.length === 0) return null;

                        const product = products[0];
                        const calc = product.calculated;
                        return (
                            <div className={'summary-box-table__container'}>
                                <p>Based on a £1,000 deposit, here's what your estimated balance would be after 12 months:</p>
                                <div className={'summary-box-table table-border example-calculation'}>
                                    <div className={'heading'}><p>Term</p></div>
                                    <div className={'heading'}><p>Deposit Amount</p></div>
                                    <div className={'heading'}><p>Balance at the end of the term</p></div>
                                    <div className={'content'}>1 year</div>
                                    <div className={'content'}>
                                        £1,000
                                    </div>
                                    <div className={'content'}>
                                        {calc && product ? formatCurrency(calc.saved) : 'Contact us for projection'}
                                    </div>
                                </div>
                                <p className="margin-sml-top">The example above is illustrative only and based on individual circumstances. It also assumes the deposit is made on the day the account is opened. The example shows total interest earned, please note that interest will be paid annually.</p>
                            </div>
                        )
                    }
                },
                {
                    title: 'How do I open and manage my account?',
                    body: function (_products, featureEnabled) {
                        return (
                            <div>
                                <p>You can open and manage your account online. If you&apos;re an existing Investec Save client, log in to your dashboard to apply.</p>
                                <br></br>
                                <p><strong>If you&apos;re new to Investec Save, you&apos;ll need:</strong></p>
                                <ul className="margin-sml-top">
                                    <li>the sort code and account number of your UK current account (this will become your linked account)</li>
                                    <li>a UK smartphone</li>
                                    <li>your National Insurance number</li>
                                    <li>a valid passport or photocard driving licence from a supported country</li>
                                </ul>
                                
                                <p className="margin-sml-top"><strong>To be eligible, you must:</strong></p>
                                <ul>
                                    <li>be a UK resident with a UK residential address</li>
                                    <li>be 18 or over</li>
                                    <li>have at least £1,000 to save</li>
                                    <li>stay within the overall ISA subscription limit for the current tax year when you apply</li>
                                    <li>make sure your total ISA subscriptions across all providers stay within the £20,000 annual allowance.</li>
                                </ul>
                            
                                <p className="margin-sml-top"><strong>Minimum and maximum amounts</strong></p>
                                <ul>
                                    <li>The minimum deposit to open the account is £1,000.</li>
                                    <li>The maximum balance you can hold in the account at any time is £1,000,000. However, you cannot exceed your annual ISA allowance in any tax year.</li>
                                </ul>
                                <br></br>                            
                                <p><strong>Funding your account</strong></p>
                                <p>{featureEnabled('EnableFisaMaturity')
                                    ? "Once the account is open, you’ll have seven calendar days to fund it with at least £1,000. You can fund it from your linked account or, if you have one, your Online Flexi Saver or you can transfer funds from an Investec Save Instant Access Cash ISA. Multiple deposits are allowed during this window, up to your £20,000 annual ISA allowance."
                                    : <>Once the account is open, you&apos;ll have seven calendar days to fund it with at least £1,000. You can fund it from your linked account or, if you have one, your Online Flexi Saver. Multiple deposits are allowed during this window, up to your £20,000 annual ISA allowance.</>}</p>
                                <br></br>
                                <p>You can also transfer an existing cash ISA with another provider when you open your account.</p>
                                <br></br>
                                <p>Your ISA subscription begins on the day we receive your first deposit, so please make sure your total deposits stay within your ISA allowance.</p>
                                <br></br>
                                <p>If your balance is below £1,000 by day seven, we&apos;ll close your account and return your funds, including any interest earned, to your linked account. We will not close your account if you have requested an ISA transfer from another provider which is still in progress.</p>
                                <br></br>
                                <p><strong>Please note:</strong></p>
                                <p>We only accept applications for individual accounts.</p>
                            </div>
                        )
                    }
                },
                {
                    title: 'Can I withdraw money?',
                    body: function (_products, featureEnabled) {
                        return (
                            <div>
                                <p>You won&apos;t be able to make withdrawals during the term, but you can close your account at any time.</p>
                                <ul>
                                    <li><strong>If you close your account within the 14-day cancellation period</strong><br/>
                                        {featureEnabled('EnableFisaMaturity')
                                            ? "There’s no early closure fee and your funds won’t count towards your ISA allowance. Your full balance, including any interest earned, will be returned to your linked account. If you have an Instant Access Cash ISA with us, you can instead transfer the balance to that account and any interest will be paid to your linked account. Any interest earned during this period will be tax free."
                                            : <>There&apos;s no early closure fee and your funds won&apos;t count towards your ISA allowance. Your full balance, including any interest earned, will be returned to your linked account. Any interest earned during this period will be tax free.</>}
                                    </li>
                                    <br></br>
                                    <li><strong>If you close your account after the cancellation period</strong><br/>
                                        An early closure fee equal to 90 days&apos; interest will apply. We&apos;ll deduct your early closure fee from your balance. The remaining balance, after the fee is deducted, will be returned to your linked account or transferred to your new provider if you choose to transfer your ISA. Any interest accrued up to the day before closure will be paid into your linked account. If the early closure fee is more than the interest you have earned, you&apos;ll get back less money than you paid into your account.
                                    </li>
                                    <br></br>
                                    <li><strong>If you close your account after the cancellation period without transferring your ISA to another provider</strong><br/>
                                                The balance returned to your linked account will lose its tax-free status and contributions made in the same tax year will still count towards your annual ISA allowance. To maintain the tax-free status, you can transfer the balance to another provider, but an early closure fee will apply.
                                    </li>                                    
                                </ul>
                                <br></br>
                                <p>You can close your account via your online dashboard.</p>
                                <br></br>
                                <p>If you&apos;d like to close your account while a transfer from another provider is in progress, you&apos;ll need to cancel your transfer request first with your existing provider.</p>
                                <br></br>
                                <p>If you&apos;d like to close your account by transferring it to another ISA provider, you&apos;ll need to request the transfer with your new ISA provider.</p>
                                <br></br>
                                <p>{featureEnabled('EnableFisaMaturity')
                                    ? "We’ll contact you 14 days before the end of your fixed term to remind you that it is ending. By default, your balance will move into an Investec Save Instant Access Cash ISA, and any interest earned will be paid into your linked account. From there, you can choose to transfer your savings to another ISA provider if you prefer."
                                    : <>At the end of your fixed term, we&apos;ll get in touch to explain your options. By default, your balance will move into an Investec Save Instant Access Cash ISA, and any interest earned will be paid into your linked account. From there, you can choose to transfer your savings to another ISA provider if you prefer.</>}</p>
                            </div>
                        )
                    }
                },
                {
                    title: 'Additional information',
                    body: function (_products, _featureEnabled) {
                        return (
                            <div>
                                <p>ISA stands for Individual Savings Account. It&apos;s a savings or investment account that offers tax benefits.</p>
                                <p className="margin-sml-top">Normally, interest earned on savings accounts is taxable if it exceeds your Personal Savings Allowance (PSA). But with an ISA, the interest you earn doesn&apos;t count towards your PSA, so you won&apos;t pay income tax on any returns your ISA makes.</p>
                                <p className="margin-sml-top">Terms and conditions apply.</p>
                            </div>
                        )
                    }
                }
            ]
        }
    ];

    if (!data)
        return (<></>);

    if (!customerData || customerData.loading || !customerData.data)
        return <Loading message="We're loading the product details..." />;

    const products = customerData.data.customer.products.filter(obj => {
        return obj.type === mapProductTypeFromUrl(window.location.pathname);
    });

    if (!products)
        return (<></>);

    const newProducts = customerData.data.customer.newAccounts.filter(obj => {
        return obj.productType === mapProductTypeFromUrl(window.location.pathname);
    });

    if (!newProducts || !newProducts.find(p => p.qualify) || existingApplicationAcceptedInSession(newProducts))
        return (<>Product not available</>);

    //TODO: find a better way to do this - for now, we treat the 1st product as a "template" for any properties which are generic across the product type 
    const product = products[0];

    const detail = summaryCopy.find(obj => {
        return obj.productType === mapProductTypeFromUrl(window.location.pathname);
    });

    /** @returns {SummaryBoxProductData[]} */
    const mapSummaryBoxProductData = (/** @type {Product[]} */ products) => {
        const periodicAmount = 0.00;

        const summaryBoxProductData = products.map(product => {
            // Use £1,000 deposit for FISA, £5,000 for other products
            const deposit = product.type === productTypes.FixedRateCashISA ? 1000.00 : 5000.00;
            
            return {
                code: product.code,
                productTerm: product.productTerm,
                name: product.name,
                rate: product.rate,
                issueNumber: product.issueNumber,
                aerRate: product.aerRate,
                rateChanges: product.rateChanges,
                rateChangeNotification: product.rateChangeNotification,
                calculated: calculations.saveRegularly(
                    periodicAmount,
                    deposit,
                    product.productTerm ? convertProductTermToMonths(product.productTerm) : 12,
                    Number(product.rate),
                    product.type === productTypes.FixedTerm || product.type === productTypes.FixedRateCashISA)
            }
        });

        return summaryBoxProductData;
    }

    const goToApplicationForm = (/** @type {string} */ url, /** @type {string} */ region, /** @type {Product} */ product) => {
        analytics.trackAnalyticsClickEvent(
            `apply for an ${product.name.toLowerCase()}`,
            url,
            region,
            product.code,
            '',
            {},
            'link click',
            `investec-save|click|apply-now_${product.name.toLowerCase()}`,
            'new application'
        );


        window.location.href = url;
    };
    /**
     * Type guard for product data: returns array of card objects
     */
    /**
     * Explicit type for product card data
     */
    /** @typedef {Object} ProductCardData
     * @property {string} DisplayName
     * @property {string} Code
     * @property {string} Apply
     * @property {string} ApplyButtonLabel
     * @property {() => void} ApplyOnClick
     * @property {JSX.Element} Summary
     * @property {JSX.Element} HowThisWorks
     * @property {JSX.Element} ImportantInformation
     */
    /**
     * @param {any} val
     * @returns {ProductCardData[]}
     */
    function toProductDataArray(val) {
        if (Array.isArray(val)) return val;
        if (val && typeof val === 'object') return [val];
        return [];
    }
    /** @type {ProductCardData[]} */
    const newProductDataArr = toProductDataArray(data(products));
    const secondaryApplicationsDisabled = !isSecondaryAccountEnabled(product.type);

    return (
        <div className="my-profile my-profile--no-left-padding my-profile--my-account">
            {newProductDataArr.map((newProductData, idx) => (
                <div key={idx} className="newaccount-explorer__card" style={{marginBottom: '2rem'}}>
                    {/* Show regular title for all products on detail page */}
                    <div className="newaccount-explorer__title">
                        Open a new {newProductData.DisplayName}
                    </div>
                    <div className="newaccount-explorer__body">
                        {newProductData.Summary}
                    </div>
                    {product.type === productTypes.FixedRateCashISA && <IsaTransferSection />}
                    <div className='newaccount-explorer__item-wrapper'>
                        <div className='newaccount-explorer__item newaccount-explorer__item__chink__left'>
                            <div className='newaccount-explorer__body'>
                                {newProductData.HowThisWorks}
                            </div>
                        </div>
                        <div className='newaccount-explorer__item newaccount-explorer__item__chink__right'>
                            <div className='newaccount-explorer__body'>
                                {newProductData.ImportantInformation}
                            </div>
                        </div>
                    </div>
                    <div className="newaccount-explorer__body">
                        <p>For your convenience, we&apos;ve also included the same summary box below, to make sure you understand and are comfortable with how your new account will work.</p>
                    </div>
                    {newProductData.ApplyButtonLabel && <div className="newaccount-explorer__button">
                        <Button label={newProductData.ApplyButtonLabel}
                            dataAnalyticsItemClicked={`Apply for ${newProductData.DisplayName}-SEC`}
                            onClick={() => goToApplicationForm(newProductData.Apply, "top", product)}
                            disabled={secondaryApplicationsDisabled}
                        />
                    </div>}
                    {secondaryApplicationsDisabled && newProductData.ApplyButtonLabel && (
                        <ApplicationsDisabledNotice className="newaccount-explorer__inline-notice" />
                    )}
                    <div className="">
                        {detail && <SummaryBox data={detail.summaries} products={mapSummaryBoxProductData(products)} featureEnabled={featureEnabled} />}
                    </div>
                </div>
            ))}
            <div className="newaccount-explorer__aer">
                <p className="my-profile__aer-info">* AER stands for the Annual Equivalent Rate and illustrates what the interest rate would be if interest was paid and compounded once a year.
                    <br /><br />The AER is intended to be an indicative rate to help you compare the return on different savings products.
                    <br /><br />
                    ^ Gross p.a. is the rate of interest applied to your account before any tax is deducted.</p>
            </div>
        </div>
    );
};

export default withFeatureFlags(NewAccountExplorer);
